import { Component } from 'react'
import styled from 'styled-components'
import { X, Check } from 'lucide-react'
import { ContentStyle } from './index'
import { Button } from '../Library/Components'

/**
 * QuickAddModal - Componente Modal riutilizzabile per aggiungere rapidamente nuovi elementi
 *
 * @param {boolean} isOpen - Controlla la visibilità del modal
 * @param {function} onClose - Callback chiamata quando il modal viene chiuso
 * @param {function} onSave - Callback chiamata quando si salva (riceve object con valori dei campi)
 * @param {string} title - Titolo del modal
 * @param {array} fields - Array di definizioni campi
 * @param {boolean} isLoading - Mostra stato di caricamento sui bottoni
 */

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.3)'};
  backdrop-filter: blur(2px);
  z-index: 998;
`

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 450px;
  max-width: 90vw;
  background: ${props => props.theme.colors.background.primary};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 999;
  padding: 1.5rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 0.25rem;
  color: ${props => props.theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.background.secondary};
    color: ${props => props.theme.colors.text.primary};
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`

const HelperText = styled.p`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.secondary};
  opacity: 0.8;
  margin: 0.375rem 0 0 0;
`

const Footer = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`

export default class QuickAddModal extends Component {
  constructor (props) {
    super(props)

    // Inizializza state dinamicamente basandosi sui fields
    const initialValues = {}
    props.fields.forEach(field => {
      initialValues[field.name] = ''
    })

    this.state = {
      values: initialValues
    }
  }

  componentDidUpdate (prevProps) {
    // Reset valori quando il modal si apre
    if (this.props.isOpen && !prevProps.isOpen) {
      const initialValues = {}
      this.props.fields.forEach(field => {
        initialValues[field.name] = ''
      })
      this.setState({ values: initialValues })
    }
  }

  handleChange = (fieldName, value) => {
    this.setState(prevState => ({
      values: {
        ...prevState.values,
        [fieldName]: value
      }
    }))
  }

  handleSave = () => {
    const { values } = this.state
    const { onSave } = this.props

    // Chiama il callback con i valori
    if (onSave) {
      onSave(values)
    }
  }

  handleClose = () => {
    const { onClose } = this.props
    if (onClose) {
      onClose()
    }
  }

  handleBackdropClick = () => {
    this.handleClose()
  }

  handleModalClick = (e) => {
    // Previeni la chiusura quando si clicca sul modal stesso
    e.stopPropagation()
  }

  render () {
    const { isOpen, title, fields, isLoading } = this.props
    const { values } = this.state

    if (!isOpen) {
      return null
    }

    return (
      <>
        <Overlay onClick={this.handleBackdropClick} />

        <ModalContainer onClick={this.handleModalClick}>
          <Header>
            <Title>{title}</Title>
            <CloseButton onClick={this.handleClose} disabled={isLoading}>
              <X size={20} />
            </CloseButton>
          </Header>

          <Body>
            {fields.map((field, index) => (
              <FieldContainer key={field.name || index}>
                <Label>{field.label}</Label>
                <ContentStyle.Text
                  type={field.type || 'text'}
                  placeholder={field.placeholder || ''}
                  value={values[field.name] || ''}
                  onChange={(e) => this.handleChange(field.name, e.target.value)}
                  style={{ width: '100%' }}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  disabled={isLoading}
                />
                {field.helperText && (
                  <HelperText>{field.helperText}</HelperText>
                )}
              </FieldContainer>
            ))}
          </Body>

          <Footer>
            <Button
              type='secondary'
              outline='true'
              onClick={this.handleClose}
              disabled={isLoading}
            >
              Annulla
            </Button>
            <Button
              type='success'
              onClick={this.handleSave}
              disabled={isLoading}
            >
              <Check size={18} /> {isLoading ? 'Salvataggio...' : 'Salva'}
            </Button>
          </Footer>
        </ModalContainer>
      </>
    )
  }
}
